<template>
  <div class="flex flex-col">
    <div
      class="flex w-full pt-3 space-x-2 border-gray-400"
      :class="registerCtaTop ? 'order-1 border-b pb-3 justify-center' : 'order-2  border-t justify-between'"
    >
      <p class="fluid-sm leading-tight block">{{ $t('account.modal.noAccount') }}</p>
      <nuxt-link
        class="fluid-sm leading-tight block font-medium text-black underline underline-offset-2 hover:no-underline focus-visible:focus-ring"
        :to="$localeRootPath('/account/register')"
        @click="handleRegisterClick"
        >{{ $t('account.modal.registerNow') }}</nuxt-link
      >
    </div>

    <form
      class="flex flex-col items-center"
      :class="registerCtaTop ? 'order-2' : 'order-1'"
      @submit.prevent="handleLogin"
    >
      <h3 class="fluid-xl leading-tight font-medium mt-10 mb-3 text-center">
        <slot>{{ $t('account.login') }}</slot>
      </h3>

      <SmallNotice
        v-if="maintenance?.active"
        class="mb-4"
        type="error"
        :message="maintenance?.message ?? $t('account.modal.maintenance')"
      />

      <div v-if="!maintenance?.active" class="w-full mb-6">
        <SocialLoginButtons />
      </div>

      <FormTextInput
        v-model="form.email"
        id="login-form-email"
        :error="form.errors.email"
        :disabled="maintenance?.active"
        :label="$t('account.labels.email')"
        name="email"
        type="email"
        autocomplete="email"
        required
      />

      <FormTextInput
        v-model="form.password"
        id="login-form-password"
        :error="form.errors.password"
        :disabled="maintenance?.active"
        :label="$t('account.labels.password')"
        name="password"
        type="password"
        autocomplete="current-password"
        required
      />

      <GenericFormError v-if="form.errors.message">
        {{ form.errors.message }}
      </GenericFormError>

      <nuxt-link
        v-if="forgotPasswordOverButton"
        :to="$localeRootPath('/account/reset')"
        class="fluid-sm leading-tight h-4 font-medium text-black border-b border-black"
        >{{ $t('account.modal.forgotPassword') }}</nuxt-link
      >

      <FormButton :disabled="maintenance?.active" class="self-center" :is-loading="form.processing">{{
        $t('account.login')
      }}</FormButton>

      <div v-if="!forgotPasswordOverButton && !maintenance?.active" class="space-x-2">
        <span class="fluid-sm leading-tight">
          {{ $t('account.modal.forgotPassword') }}
        </span>
        <nuxt-link
          :to="$localeRootPath('/account/reset')"
          class="fluid-sm leading-tight font-medium text-black underline underline-offset-2 hover:no-underline focus-visible:focus-ring"
          >{{ $t('account.modal.clickHere') }}</nuxt-link
        >
      </div>
    </form>
  </div>
</template>

<script setup>
import { onMounted } from 'vue';
import * as yup from 'yup';
import FormButton from '@/components/account/FormButton';
import FormTextInput from '@/components/account/FormTextInput';
import GenericFormError from '@/components/account/GenericFormError';
import SmallNotice from '@/components/forms/shared/SmallNotice.vue';
import SocialLoginButtons from '@/components/account/SocialLoginButtons.vue';
import { mapToBasicLocale } from '@/utils/i18n';
import { useForm } from '@/composables/useForm';
import { useBlackrollAuth } from '@/composables/useBlackrollAuth';
import { useAccountStore } from '@/stores/account';

const { $localeRootPath, $_gtm, $sso } = useNuxtApp();

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();

const accountStore = useAccountStore();
const maintenance = computed(() => accountStore.maintenance);

const emit = defineEmits(['success']);

// Create and get the auth composable
const { login } = useBlackrollAuth();

// Props
const props = defineProps({
  registerCtaTop: {
    type: Boolean,
    default: false,
  },
  forgotPasswordOverButton: {
    type: Boolean,
    default: false,
  },
  fromUri: {
    type: String,
    default: null,
  },
});

// Form schema
const schema = yup.object().shape({
  email: yup.string().required(t('account.fields.email.required')).email(t('account.fields.email.email')),
  password: yup.string().required(t('account.fields.password.required')),
});

// Form setup
const form = useForm(
  {
    email: '',
    password: '',
    languageCode: mapToBasicLocale(locale.value),
  },
  schema
);

// Handle register click
const handleRegisterClick = () => {
  $_gtm.push({
    event: 'gaEvent',
    event_name: 'sign_up_click',
    sign_up_click: {
      event_category: 'Account',
      event_action: 'Sign Up',
      event_label: 'Click',
    },
  });
};

// Handle login
const handleLogin = async () => {
  if (props.fromUri) {
    window.localStorage.setItem('fromUri', props.fromUri);
  }

  const isValid = await form.validate();
  if (!isValid) return;

  try {
    // Use the form submit method
    await form.submit(login(form.email, form.password, form.languageCode));

    emit('success');

    $_gtm.push({
      event: 'gaEvent',
      event_name: 'login',
      login: {
        event_category: 'Account',
        event_action: 'Login',
        event_label: 'Success',
      },
    });

    // Check for redirects from localStorage
    const redirect = window.localStorage.getItem('ow:redirect');
    if (redirect) {
      const redirectResult = $sso.processOWRedirect(redirect);

      if (redirectResult) {
        router.push(redirectResult);
      }
    } else {
      navigateTo($localeRootPath('/account/profile/details'));
    }
  } catch (error) {
    console.warn('Login error:', error);

    $_gtm.push({
      event: 'gaEvent',
      event_name: 'login_fail',
      login_fail: {
        event_category: 'Account',
        event_action: 'Login',
        event_label: 'Fail',
      },
    });
  }
};

// Initialize
onMounted(() => {
  // Check for prefilled email from query
  const prefilledEmail = route.query?.email;

  if (prefilledEmail && typeof prefilledEmail === 'string' && prefilledEmail.length > 0) {
    form.email = prefilledEmail;
  }
});
</script>
